import KaboodleToaster from "components/common/KaboodleToaster";
import { useRetryBackgroundJobByIdMutation } from "services/background-job-api";

type RetryJobButtonProps = {
  jobId: number;
  /**
   * whether the API reports this job as retryable. Retry re-dispatches the job
   * in place, which the server only supports for failed AI sort/summary jobs,
   * so every other row renders an empty cell.
   */
  canRetry: boolean;
};

export const RetryJobButton = ({ jobId, canRetry }: RetryJobButtonProps) => {
  const [retryBackgroundJob, { isLoading }] =
    useRetryBackgroundJobByIdMutation();

  if (!canRetry) {
    return null;
  }

  const handleRetry = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isLoading) return;

    try {
      await retryBackgroundJob(jobId).unwrap();
      KaboodleToaster.success(`Job ${jobId} has been queued for retry`);
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === "object" && "data" in error
          ? ((error.data as { detail?: string })?.detail ??
            "Failed to retry job")
          : "Failed to retry job";

      KaboodleToaster.error(errorMessage);
    }
  };

  return (
    <div className="background-job-retry">
      <div
        role="button"
        aria-label={`Retry job ${jobId}`}
        aria-disabled={isLoading}
        className="material-icons background-job-retry__icon"
        data-tooltip-id="retry-job-tooltip"
        data-tooltip-content={isLoading ? "Retrying..." : "Retry job"}
        data-tooltip-place="top"
        onClick={handleRetry}
      >
        {isLoading ? "hourglass_empty" : "refresh"}
      </div>
    </div>
  );
};

export default RetryJobButton;
